import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Rule of Thirds adapter.
 * Divides the element into a 3×3 grid (2 vertical + 2 horizontal lines).
 * Classic composition technique from photography and design.
 */
export class RuleOfThirdsAdapter extends GridAdapter {
  readonly type = "rule-of-thirds";
  readonly label = "3×3";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="6" width="4" height="4" fill="currentColor"/><path d="M6 3.6C6 3.03995 6 2.75992 6.10899 2.54601C6.20487 2.35785 6.35785 2.20487 6.54601 2.10899C6.75992 2 7.03995 2 7.6 2H8.4C8.96005 2 9.24008 2 9.45399 2.10899C9.64215 2.20487 9.79513 2.35785 9.89101 2.54601C10 2.75992 10 3.03995 10 3.6V5H6V3.6Z" fill="currentColor"/><path d="M6 11H10V12.4C10 12.9601 10 13.2401 9.89101 13.454C9.79513 13.6422 9.64215 13.7951 9.45399 13.891C9.24008 14 8.96005 14 8.4 14H7.6C7.03995 14 6.75992 14 6.54601 13.891C6.35785 13.7951 6.20487 13.6422 6.10899 13.454C6 13.2401 6 12.9601 6 12.4V11Z" fill="currentColor"/><path d="M2 7.6C2 7.03995 2 6.75992 2.10899 6.54601C2.20487 6.35785 2.35785 6.20487 2.54601 6.10899C2.75992 6 3.03995 6 3.6 6H5V10H3.6C3.03995 10 2.75992 10 2.54601 9.89101C2.35785 9.79513 2.20487 9.64215 2.10899 9.45399C2 9.24008 2 8.96005 2 8.4V7.6Z" fill="currentColor"/><path d="M11 6H12.4C12.9601 6 13.2401 6 13.454 6.10899C13.6422 6.20487 13.7951 6.35785 13.891 6.54601C14 6.75992 14 7.03995 14 7.6V8.4C14 8.96005 14 9.24008 13.891 9.45399C13.7951 9.64215 13.6422 9.79513 13.454 9.89101C13.2401 10 12.9601 10 12.4 10H11V6Z" fill="currentColor"/></svg>`;

  getDefaults(): Record<string, any> {
    return {
      color: "#e2e24a",
      opacity: 0.4,
      showIntersections: true,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      { type: "color", key: "color", label: "Color", default: "#e2e24a" },
      {
        type: "range",
        key: "opacity",
        label: "Opacity",
        default: 0.4,
        min: 0,
        max: 1,
        step: 0.01,
        displayMultiplier: 100,
        displayStep: 10,
      },
      { type: "toggle", key: "showIntersections", label: "Show points", default: true },
    ];
  }

  render(svg: SVGSVGElement, width: number, height: number, settings: Record<string, any>): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { color, opacity, showIntersections } = settings;

    const third_w = width / 3;
    const third_h = height / 3;

    // Vertical lines
    group.appendChild(this.createLine(third_w, 0, third_w, height, color, opacity));
    group.appendChild(this.createLine(third_w * 2, 0, third_w * 2, height, color, opacity));

    // Horizontal lines
    group.appendChild(this.createLine(0, third_h, width, third_h, color, opacity));
    group.appendChild(this.createLine(0, third_h * 2, width, third_h * 2, color, opacity));

    // Intersection points
    if (showIntersections) {
      const points = [
        [third_w, third_h],
        [third_w * 2, third_h],
        [third_w, third_h * 2],
        [third_w * 2, third_h * 2],
      ];

      for (const [cx, cy] of points) {
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", String(cx));
        circle.setAttribute("cy", String(cy));
        circle.setAttribute("r", "3");
        circle.setAttribute("fill", color);
        circle.setAttribute("fill-opacity", String(opacity * 1.5));
        group.appendChild(circle);
      }
    }
  }
}
